import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";
import { useState, useEffect } from "react";

export function HeroNew() {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = [
        "/img/透视图-无尺寸.webp",
    "/img/全景图-恢复的.webp",
    "/img/EV-场景图3.webp",
    "/img/LGV-场景图1.webp",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [images.length]);
  
  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentImageIndex}
            src={images[currentImageIndex]}
            alt="長安跨越 Electric Vehicle"
            className="w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/40 to-slate-900/80" />
      </div>

      {/* prev arrow */}
      <button
        onClick={() => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white/60 hover:text-white/90 transition-all backdrop-blur-sm"
        aria-label="Previous image"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* next arrow */}
      <button
        onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white/60 hover:text-white/90 transition-all backdrop-blur-sm"
        aria-label="Next image"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10 pt-24 pb-8 flex-1 flex flex-col justify-between min-h-screen">
        <div className="max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="inline-block mb-4 px-4 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full backdrop-blur-sm"
            >
              <span className="text-blue-400 font-medium text-sm">{t("hero.badge")}</span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="text-amber-400 text-sm md:text-base font-medium mb-3 tracking-wide"
            >
              {t("hero.agentInfo")}
            </motion.p>

            <motion.h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white drop-shadow-lg"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              {t("hero.title")}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent ml-2">
                {t("hero.titleHighlight")}
              </span>
            </motion.h1>

            <motion.p
              className="text-base md:text-lg text-gray-200 mb-6 max-w-2xl mx-auto leading-relaxed drop-shadow-md"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 justify-center"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Link to="/products">
                <Button
                  size="lg"
                  className="group bg-blue-600 hover:bg-blue-700 text-white px-6 py-5"
                >
                  {t("common.exploreModels")}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-6 py-5 backdrop-blur-sm"
                >
                  {t("common.bookTestDrive")}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="flex-1" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="max-w-7xl mx-auto w-full"
        >
          <div className="flex flex-wrap justify-center items-end gap-8 md:gap-16">
            {[
              {
                label: t("hero.range"),
                value: "350",
                unit: "km",
              },
              {
                label: t("hero.battery"),
                value: "53.58",
                unit: "kWh",
              },
              {
                label: t("hero.cargoVolume"),
                value: "6.7",
                unit: "m³",
              },
              {
                label: t("hero.weight"),
                value: "1550",
                unit: "kg",
              },
              {
                label: t("hero.dimensions"),
                value: "4800×1680×1930",
                unit: "mm",
              },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-bold text-white">
                  {item.value}
                  <span className="text-base md:text-lg text-gray-400 ml-1 font-normal">{item.unit}</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex justify-center mt-6 pb-4"
          >
            <div className="flex flex-col items-center">
              <p className="text-xs text-gray-400 mb-2">{t("common.scrollToExplore")}</p>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1.5"
              >
                <div className="w-1 h-2 bg-blue-500 rounded-full" />
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
